import { BrowserModule } from '@angular/platform-browser';
import { NgModule, ErrorHandler, Injectable } from '@angular/core';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { CommonModule, DecimalPipe } from '@angular/common';
import * as Sentry from '@sentry/browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ImageCropperComponent } from 'ngx-image-cropper';
import { provideTranslateService, TranslateModule } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { ActivitiesComponent } from './activities/activities.component';
import { ActivityListComponent } from './activities/list/activity-list.component';
import { ActivityDetailsComponent } from './activities/details/activity-details.component';
import { ActivitiesCartComponent } from './activities/cart/activities-cart.component';
import { ActivitiesCartListComponent } from './activities/cart/list/activities-cart-list.component';
import { ShoppingCartV1Component } from './shopping-cart-v1/shopping-cart-v1.component';
import { EventsComponent } from './profile/events/events.component';
import { ChangePasswordComponent } from './profile/change-password/change-password.component';
import { ProfilePageComponent } from './profile/profile-page/profile-page.component';
import { OrdersComponent } from './profile/orders/orders.component';
import { HeaderComponent } from './profile/header/header.component';
import { PagerComponent } from './profile/pager/pager.component';
import { LoginComponent } from './profile/login/login.component';
import { EventDatePipe } from './shared/event-date.pipe';
import { SafeHtmlPipe } from './shared/safe-html.pipe';
import { ContactsComponent } from './profile/contacts/contacts.component';
import { LikeUnlikeComponent } from './activities/like-unlike/like-unlike.component';
import { SubscriptionsComponent } from './profile/subscriptions/subscriptions.component';
import { ShoppingCartV2Component } from './shopping-cart-v2/shopping-cart-v2.component';
import { TwoDecNumberPipe } from './shared/two-dec-number.pipe';
import { PopupTemplateContainerComponent } from './shared/popup-template/popup-template-container.component';
import { PopupMenuDirective } from './shared/popup-menu.directive';
import { PopupTemplatePopupMenuComponent } from './shared/popup-template/popup-menu/popup-menu.component';
import { AddTextPipe } from './shared/add-text.pipe';
import { ShoppingCartV2OrderItemsCardViewComponent } from './shopping-cart-v2/shopping-cart-v2-order-items-card-view/shopping-cart-v2-order-items-card-view.component';
import { ShoppingCartV2OrderItemsCardViewRowComponent } from './shopping-cart-v2/shopping-cart-v2-order-items-card-view/shopping-cart-v2-order-items-card-view-row.component';
import { ShoppingCartV2OrderItemsTableViewComponent } from './shopping-cart-v2/shopping-cart-v2-order-items-table-view/shopping-cart-v2-order-items-table-view.component';
import { ToggleButtonComponent } from './shared/toggle-button/toggle-button.component';
import { AppLoggerService, LogLevel } from './shared/app-logger.service';
import { MultiRegistrationComponent } from './shared/multi-registration/multi-registration.component';
import { TableModule } from './table/table.module';
import { ApplyFunctionPipe } from './shared/apply-function.pipe';

Sentry.init({
    dsn: 'https://1e7e914420a562b9404f20f9eee131bd@o4507837400809472.ingest.de.sentry.io/4507893464105040',
    // Best we can do on the frontend
    environment: window.location.hostname
});

Sentry.configureScope(scope => {
    scope.addEventProcessor(event => {
        event.release = environment.sentryRelease;
        return event;
    });
});

@Injectable({
    providedIn: 'root'
})
export class SentryErrorHandler extends ErrorHandler {
    constructor(
        private appLoggerSvc: AppLoggerService,
    ) {
        super();
    }

    handleError(error: any) {
        if (environment.production) {
            const eventId = Sentry.captureException(error.originalError || error);
            // Sentry.showReportDialog({ eventId });
            this.appLoggerSvc.log(LogLevel.error, error);
        } else {
            super.handleError(error);
        }
    }
}

@NgModule({ declarations: [
        AppComponent, ShoppingCartV1Component, ShoppingCartV2Component,
        EventsComponent, ChangePasswordComponent, ProfilePageComponent, OrdersComponent, HeaderComponent, PagerComponent,
        LoginComponent, EventDatePipe, TwoDecNumberPipe, SafeHtmlPipe, ActivitiesComponent, ActivityListComponent, ActivityDetailsComponent,
        ActivitiesCartComponent, ActivitiesCartListComponent, ContactsComponent, LikeUnlikeComponent, SubscriptionsComponent,
        PopupTemplateContainerComponent, PopupTemplatePopupMenuComponent, PopupMenuDirective, AddTextPipe,
        ShoppingCartV2OrderItemsCardViewComponent, ShoppingCartV2OrderItemsCardViewRowComponent, ShoppingCartV2OrderItemsTableViewComponent,
    ],
    bootstrap: [AppComponent],
    imports: [BrowserModule,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        TranslateModule,
        ImageCropperComponent,
        ToggleButtonComponent,
        MultiRegistrationComponent,
        TableModule,
        ApplyFunctionPipe,
    ], providers: [
        AppLoggerService,
        {
            provide: ErrorHandler,
            useClass: SentryErrorHandler
        },
        DecimalPipe,
        provideHttpClient(withInterceptorsFromDi()),
        provideTranslateService({
            loader: provideTranslateHttpLoader({prefix:'./assets/i18n/', suffix:'.json'}),
        })
    ] })
export class AppModule { }
